<script lang="ts">
  /** D4 · CONTROL PANEL — its inputs, changeable by its owner or sponsor: class, body, home, card, each drive's weight and start, the
   *  player layer (the glasses), what it knows from the start. COMMIT writes one revision; every changed input becomes a file in its DB
   *  (Char_Input_changes · Expanded Tree · Additional Entries · File Logic System). Only what you touched is sent; the database diffs. */
  import { portrait, stockRecord } from '../codes'
  import type { Classes, Inputs, Rec } from '../types'

  let { rec, classes, onsave }: { rec: Rec; classes: Classes; onsave: (b: { revision: number; class?: string; inputs: Inputs }) => Promise<string> } = $props()

  const readOnly = $derived(!rec.editable || (rec.stock && rec.tenant === 0))
  let cls = $state(''), body = $state(''), home = $state(''), name = $state(''), tagline = $state(''), greeting = $state('')
  let drives = $state<Record<string, { weight: number; start: number }>>({})
  let glasses = $state(false), knowsMode = $state<'perceived' | 'all' | 'list'>('perceived'), knowsList = $state('')
  let touched = $state<Set<string>>(new Set())
  let busy = $state(false), note = $state<{ ok: boolean; text: string } | null>(null)

  /** the draft starts as what it runs as now */
  function reset() {
    cls = rec.record.class; body = rec.effective.body; home = rec.effective.home ?? ''; name = rec.effective.name ?? rec.title; tagline = rec.effective.tagline; greeting = rec.effective.greeting
    drives = Object.fromEntries(rec.effective.drives.map((d) => [d.name, { weight: d.weight, start: d.start }]))
    glasses = rec.effective.layers.includes('player')
    const k = rec.effective.knows
    knowsMode = Array.isArray(k) ? 'list' : k; knowsList = Array.isArray(k) ? k.join(', ') : ''
    touched = new Set()
  }
  $effect(() => { void rec.record.revision; void rec.tenant; reset() })
  const touch = (k: string) => { touched = new Set([...touched, k]) }

  /** a new class brings its own drives: the draft shows its stock values for drives it adds, keeps yours where the name is shared */
  function pickClass(c: string) {
    cls = c; touch('class')
    const stock = stockRecord(c, classes).effective.drives
    drives = Object.fromEntries(stock.map((d) => [d.name, drives[d.name] && touched.has(`drive.${d.name}.weight`) ? drives[d.name] : { weight: d.weight, start: d.start }]))
  }

  async function commit() {
    const inputs: Inputs = {}
    if (touched.has('body')) inputs.body = body
    if (touched.has('home')) inputs.home = home
    if (touched.has('name')) inputs.name = name
    if (touched.has('tagline')) inputs.tagline = tagline
    if (touched.has('greeting')) inputs.greeting = greeting
    for (const [n, d] of Object.entries(drives)) {
      const x: { weight?: number; start?: number } = {}
      if (touched.has(`drive.${n}.weight`)) x.weight = d.weight
      if (touched.has(`drive.${n}.start`)) x.start = d.start
      if (Object.keys(x).length) (inputs.drives ??= {})[n] = x
    }
    if (touched.has('layers')) inputs.layers = glasses ? ['world', 'player'] : ['world']
    if (touched.has('knows')) inputs.knows = knowsMode === 'list' ? knowsList.split(',').map((s) => s.trim()).filter(Boolean) : knowsMode
    busy = true; note = null
    try {
      const said = await onsave({ revision: rec.record.revision, ...(touched.has('class') && cls !== rec.record.class ? { class: cls } : {}), inputs })
      note = { ok: true, text: said }
    } catch (e) {
      note = { ok: false, text: (e as Error).message }
    } finally { busy = false }
  }
</script>

<div class="panel">
  {#if readOnly}
    <p class="ro frame">{rec.tenant === 0 ? 'A stock class is the template every NPC is stamped from — it is not changed here. Open one of your NPCs to change it.'
      : 'You can view this NPC but not change it — only its owner or its sponsor can.'}</p>
  {/if}
  <fieldset disabled={readOnly || busy}>
    <section class="frame">
      <h3 class="caps">Class</h3>
      <div class="classes">
        {#each Object.entries(classes.classes) as [k, c]}
          <button class="pick" class:on={cls === k} onclick={() => pickClass(k)}>{c.title}</button>
        {/each}
      </div>
      {#if cls !== rec.record.class}<p class="warn">Changing its class rewrites its logic. Drives the new class doesn't have are dropped, and the drop goes in its record.</p>{/if}
      <h3 class="caps">Body</h3>
      <div class="bodies scroll">
        {#each classes.bodies as b}
          <button class="b" class:on={body === b} onclick={() => { body = b; touch('body') }} title={b}><img src={portrait(b)} alt={b} loading="lazy" /></button>
        {/each}
      </div>
    </section>
    <section class="frame">
      <h3 class="caps">Card &amp; home</h3>
      {#if rec.mco}<p class="mco">{rec.mco.pushes ? 'Synced with its MCO profile' : 'Read from your MCO profile (edit it on MegaCity)'} — <a href={`https://megacity.online/${rec.mco.userName}`} target="_blank" rel="noopener">@{rec.mco.userName}</a></p>{/if}
      <label>Name{#if rec.mco?.pushes} <small>→ MCO name</small>{/if}<input maxlength="80" bind:value={name} oninput={() => touch('name')} /></label>
      <label>Tagline{#if rec.mco?.pushes} <small>→ MCO work title</small>{/if}<input maxlength="80" bind:value={tagline} oninput={() => touch('tagline')} /></label>
      <label>Greeting{#if rec.mco?.pushes} <small>→ MCO biography</small>{/if}<textarea maxlength="160" rows="2" bind:value={greeting} oninput={() => touch('greeting')}></textarea></label>
      <label>Home — where it lives while no one watches
        <select bind:value={home} onchange={() => touch('home')}>
          <option value="">Unplaced — it waits in the database</option>
          {#each classes.places ?? [] as p}<option value={p.home}>{p.title} — {p.home}</option>{/each}
          {#if home && !(classes.places ?? []).some((p) => p.home === home)}<option value={home}>{home}</option>{/if}
        </select>
      </label>
      <h3 class="caps">Perceives</h3>
      <label class="tog"><input type="checkbox" bind:checked={glasses} onchange={() => touch('layers')} /><span>The player layer — <b>the glasses</b>: quests, markers, what players see</span></label>
      <h3 class="caps">Knows from the start</h3>
      <div class="row">
        {#each ['perceived', 'all', 'list'] as m}<label class="tog"><input type="radio" name="knows" value={m} bind:group={knowsMode} onchange={() => touch('knows')} /> {m === 'perceived' ? 'Only what it sees' : m === 'all' ? 'Everything' : 'A list'}</label>{/each}
      </div>
      {#if knowsMode === 'list'}<input placeholder="counter, bench, coffee" bind:value={knowsList} oninput={() => touch('knows')} />{/if}
    </section>
    <section class="frame drives">
      <h3 class="caps">Drives</h3>
      {#each Object.entries(drives) as [n, d]}
        <div class="drive">
          <span class="caps n">{n}</span>
          <label>weight <input type="range" min="0" max="5" step="0.1" bind:value={d.weight} oninput={() => touch(`drive.${n}.weight`)} /><span class="mono">×{d.weight}</span></label>
          <label>starts <input type="range" min="0" max="100" step="1" bind:value={d.start} oninput={() => touch(`drive.${n}.start`)} /><span class="mono">{d.start}</span></label>
        </div>
      {/each}
    </section>
  </fieldset>
  {#if !readOnly}
    <div class="bar">
      <span class="muted mono">revision {rec.record.revision} · {touched.size ? `${touched.size} input${touched.size === 1 ? '' : 's'} touched` : 'nothing changed yet'}</span>
      {#if note}<span class:ok={note.ok} class:err={!note.ok}>{note.text}</span>{/if}
      <button class="btn" onclick={reset} disabled={!touched.size || busy}>Revert</button>
      <button class="btn solid" onclick={commit} disabled={!touched.size || busy}>{busy ? 'Writing…' : 'Commit'}</button>
    </div>
  {/if}
</div>

<style>
  .panel { padding: 18px; display: flex; flex-direction: column; gap: 14px; }
  fieldset { border: 0; margin: 0; padding: 0; display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 14px; align-items: start; }
  fieldset:disabled { opacity: .6; }
  section { background: var(--panel); padding: 12px 14px; display: flex; flex-direction: column; gap: 8px; }
  h3 { margin: 6px 0 2px; font-size: 13px; color: var(--muted); font-weight: 600; }
  .classes, .row { display: flex; gap: 8px; flex-wrap: wrap; }
  .pick { border: 1px solid var(--faint); padding: 4px 12px; text-transform: uppercase; letter-spacing: .05em; color: var(--muted); }
  .pick.on { border-color: var(--cyan); color: var(--cyan); background: rgba(34, 211, 238, .1); }
  .warn { color: var(--amber); margin: 0; font-size: 14px; }
  .bodies { display: grid; grid-template-columns: repeat(auto-fill, minmax(54px, 1fr)); gap: 6px; max-height: 190px; overflow: auto; }
  .b { border: 1px solid #22303b; background: #0d1217; aspect-ratio: 1; overflow: hidden; }
  .b img { width: 100%; height: 100%; object-fit: cover; display: block; }
  .b.on { border: 2px solid var(--cyan); }
  label { display: flex; flex-direction: column; gap: 3px; color: var(--muted); font-size: 13px; }
  .tog { flex-direction: row; align-items: center; gap: 8px; color: var(--text); font-size: 15px; }
  .tog b { color: var(--cyan); font-weight: 600; }
  .drives { grid-column: 1 / -1; }
  .drive { display: grid; grid-template-columns: 110px 1fr 1fr; gap: 14px; align-items: center; border-bottom: 1px solid #18212a; padding: 4px 0; }
  .drive label { flex-direction: row; align-items: center; gap: 8px; }
  .drive input[type=range] { flex: 1; accent-color: var(--cyan); }
  .n { color: var(--text); font-weight: 600; }
  .bar { display: flex; gap: 12px; align-items: center; justify-content: flex-end; flex-wrap: wrap; position: sticky; bottom: 0; background: var(--bg); padding: 8px 0; border-top: 1px solid var(--line-dim); }
  .bar .muted { margin-right: auto; }
  .ok { color: var(--green); } .err { color: var(--red); }
  .ro { margin: 0; padding: 10px 14px; color: var(--amber); background: var(--panel); }
  .mco { margin: 0 0 4px; font-size: 13px; color: var(--muted); } .mco a { color: var(--cyan); }
  label small { color: var(--faint); font-size: 11px; text-transform: none; letter-spacing: 0; }
  @media (max-width: 700px) { .drive { grid-template-columns: 1fr; gap: 2px; } }
</style>
